import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';

import { VirtualScrollOuterElement } from './VirtualScrollOuterElement';
import { useWindowResizeListener } from './hooks/useWindowResizeListener';
import { useVirtualScrollWrapperContext } from './context';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

export interface ListChildComponentProps<TItemData> {
  index: number;
  style: CSSProperties;
  data: TItemData;
}

export interface VirtualFixedListProps<TItemData> {
  readonly children: React.ComponentType<ListChildComponentProps<TItemData>>;
  readonly itemSize: (params: { containerWidth: number; containerHeight: number }) => number;
  readonly itemCount: number;
  readonly overscanCount?: number;
  readonly itemData?: TItemData;
}

export const VirtualFixedList = <TItemData,>({ itemSize: itemSizeCallback, ...props }: VirtualFixedListProps<TItemData>) => {
  const { virtualScrollWrapperElement } = useVirtualScrollWrapperContext();
  const listOuterElementRef = useRef<HTMLElement | null>(null);
  const [listHeight, setListHeight] = useState(0);
  const [itemSize, setItemSize] = useState(0);

  const recalculateSizes = useEventCallback(() => {
    const listOuterElement = listOuterElementRef.current;

    if (virtualScrollWrapperElement && listOuterElement) {
      const nextItemSize = itemSizeCallback({ containerWidth: listOuterElement.offsetWidth, containerHeight: virtualScrollWrapperElement.clientHeight });
      setItemSize(nextItemSize);
      setListHeight(virtualScrollWrapperElement.clientHeight);
    }
  });

  useWindowResizeListener(() => {
    recalculateSizes();
  });

  useEffect(() => {
    recalculateSizes();
  }, [recalculateSizes, virtualScrollWrapperElement]);

  return (
    <FixedSizeList
      outerElementType={VirtualScrollOuterElement}
      outerRef={listOuterElementRef}
      width="100%"
      height={listHeight}
      itemSize={itemSize}
      {...props}
    />
  );
};
